import React, { useEffect, useState } from 'react';
import { Loan } from '../types/loan';
import { XIcon, PhoneIcon, CopyIcon, TargetIcon } from 'lucide-react';
interface ActionModalProps {
  isOpen: boolean;
  loan: Loan | null;
  onClose: () => void;
  onLogCall: (loan: Loan) => void;
  onCloneOpportunity: (loan: Loan) => void;
  onSetTargetRate: (loan: Loan, rate: number) => void;
}
export function ActionModal({
  isOpen,
  loan,
  onClose,
  onLogCall,
  onCloneOpportunity,
  onSetTargetRate
}: ActionModalProps) {
  const [targetRateInput, setTargetRateInput] = useState('');
  useEffect(() => {
    if (loan) {
      setTargetRateInput(loan.targetRate?.toFixed(3) ?? '');
    }
  }, [loan]);
  if (!isOpen || !loan) return null;
  const handleSetTargetRate = () => {
    const rate = parseFloat(targetRateInput);
    if (!isNaN(rate) && rate > 0 && rate < 20) {
      onSetTargetRate(loan, rate);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-66">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-67">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-68">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-69">
            Take Action: {loan.borrowerName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100" data-id="element-70">
            <XIcon className="w-5 h-5" data-id="element-71" />
          </button>
        </div>

        <div className="p-6 space-y-4" data-id="element-72">
          <p className="text-sm text-gray-500 mb-6" data-id="element-73">
            Select an action to proceed with this refinance opportunity.
          </p>

          {/* Target Rate Section */}
          <div className="p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-74">
            <div className="flex items-center gap-3" data-id="element-75">
              <div className="bg-emerald-100 text-emerald-600 p-2.5 rounded-lg" data-id="element-76">
                <TargetIcon className="w-5 h-5" data-id="element-77" />
              </div>
              <div data-id="element-78">
                <div className="font-medium text-gray-900" data-id="element-79">Set Target Rate</div>
                <div className="text-xs text-gray-500" data-id="element-80">
                  Set the refinance target rate for this borrower
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-81">
              <div className="relative flex-1" data-id="element-82">
                <input type="number" step="0.125" min="0" max="20" value={targetRateInput} onChange={e => setTargetRateInput(e.target.value)} placeholder="e.g. 5.500" className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all tabular-nums" data-id="element-83" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" data-id="element-84">
                  %
                </span>
              </div>
              <button onClick={handleSetTargetRate} disabled={!targetRateInput || isNaN(parseFloat(targetRateInput))} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-85">
                Save
              </button>
            </div>
            {loan.targetRate && <p className="text-xs text-gray-500" data-id="element-86">
                Current target:{' '}
                <span className="font-medium text-emerald-700" data-id="element-87">
                  {loan.targetRate.toFixed(3)}%
                </span>
              </p>}
          </div>

          <button onClick={() => onCloneOpportunity(loan)} className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 transition-all group" data-id="element-88">
            <div className="flex items-center gap-4" data-id="element-89">
              <div className="bg-cyan-100 text-cyan-600 p-2.5 rounded-lg group-hover:bg-cyan-200 transition-colors" data-id="element-90">
                <CopyIcon className="w-5 h-5" data-id="element-91" />
              </div>
              <div className="text-left" data-id="element-92">
                <div className="font-medium text-gray-900" data-id="element-93">
                  Create Opportunity
                </div>
                <div className="text-xs text-gray-500" data-id="element-94">
                  Clone this loan to create a new refinance opportunity
                </div>
              </div>
            </div>
          </button>

          <button onClick={() => onLogCall(loan)} className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-violet-500 hover:bg-violet-50 transition-all group" data-id="element-95">
            <div className="flex items-center gap-4" data-id="element-96">
              <div className="bg-violet-100 text-violet-600 p-2.5 rounded-lg group-hover:bg-violet-200 transition-colors" data-id="element-97">
                <PhoneIcon className="w-5 h-5" data-id="element-98" />
              </div>
              <div className="text-left" data-id="element-99">
                <div className="font-medium text-gray-900" data-id="element-100">Log a Call</div>
                <div className="text-xs text-gray-500" data-id="element-101">
                  Record notes from a recent conversation
                </div>
              </div>
            </div>
          </button>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end" data-id="element-102">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-103">
            Cancel
          </button>
        </div>
      </div>
    </div>;
}